import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Empty,
  Flex,
  Result,
  Spin,
  Switch,
  Table,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import Chart from "react-apexcharts";
import { useEffect, useMemo, useState } from "react";
import Filters from "../../components/Filters";
import { useAuth } from "../../context/AuthContext";
import useDerivedMetricsV2 from "../../hooks/useDerivedMetricsV2";
import useGeneralRevenueGoalChart from "../../hooks/useGeneralRevenueGoalChart";
import useGeneralSellerPerformance from "../../hooks/useGeneralSellerPerformance";
import useHomeConversionV2 from "../../hooks/useHomeConversionV2";
import useHomeFiliais from "../../hooks/useHomeFiliais";
import useHomeGoalsV2 from "../../hooks/useHomeGoalsV2";
import useHomeRevenueV2 from "../../hooks/useHomeRevenueV2";
import useRScoreV2 from "../../hooks/useRScoreV2";
import { HOME_DASHBOARD_METRICS_DESCRIPTION } from "../../utils/constants";
import formatarDuracao from "../../utils/formatarDuracao";
import RevenuePeriodSelector from "../revenue/RevenuePeriodSelector";
import RScorePanel from "./RScorePanel";
import "./general.css";
import {
  conversaoPorFilial,
  derivadasPorFilial,
  detalheDasOportunidades,
  faturamentoPorFilial,
  percentualDaMetaPorFilial,
  percentualPresencial,
  periodoSemiaberto,
  serieDeMetaLegado,
  serieDeMetaV2,
  tipoDoModo,
} from "./homeV2Helpers";
import { classeDoRscore } from "./rscoreClassificacao";

const GOAL_STATUS_COLOR = {
  green: "#52c41a",
  yellow: "#faad14",
  red: "#ff4d4f",
  neutral: "#bfbfbf",
};

function goalAttainmentStatusFromPercent(percent) {
  if (percent === null || percent === undefined) return "neutral";
  if (percent >= 100) return "green";
  if (percent >= 95) return "yellow";
  return "red";
}

const { Title, Text } = Typography;

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const numberFormatter = new Intl.NumberFormat("pt-BR");
const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const SemValor = () => (
  <Text type="secondary" style={{ fontSize: 13 }}>
    --
  </Text>
);

const celulaDaRota = (carregando, valor, formatar) => {
  if (carregando) return <Spin size="small" />;
  if (valor === null || valor === undefined) return <SemValor />;
  return formatar(valor);
};

const decimal = (valor) =>
  Number(valor).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getDefaultGeneralRange = () => [
  dayjs().startOf("month").format("YYYY-MM-DD"),
  dayjs().endOf("month").format("YYYY-MM-DD"),
];

const getGoalBarColor = (actualValue, targetValue) => {
  if (targetValue <= 0) {
    return "#cbd5e1";
  }

  const attainment = (actualValue / targetValue) * 100;

  if (attainment >= 100) {
    return "#93c5a5";
  }
  if (attainment >= 95) {
    return "#f3d48a";
  }
  return "#e7a4a4";
};

const buildDateRangeParams = (selectedDateRange) => {
  if (!Array.isArray(selectedDateRange)) {
    return { startDate: null, endDate: null };
  }

  const [startDate, endDate] = selectedDateRange;

  if (!startDate || !endDate) {
    return { startDate: null, endDate: null };
  }

  return {
    startDate: `${startDate}T00:00`,
    endDate: `${endDate}T23:59`,
  };
};

export default function General() {
  const { user } = useAuth();
  const canViewGeral = Boolean(
    user?.is_superuser || user?.is_client_admin || user?.is_admin
  );
  const [selectedDateRange, setSelectedDateRange] = useState(
    getDefaultGeneralRange
  );

  const handlePreviousPeriod = () => {
    const [start, end] = selectedDateRange;
    const startDay = dayjs(start);
    const endDay = dayjs(end);
    const isFullCalendarYear =
      startDay.month() === 0 &&
      startDay.date() === 1 &&
      endDay.month() === 11 &&
      endDay.date() === 31 &&
      startDay.year() === endDay.year();
    const isFullCalendarMonth =
      !isFullCalendarYear &&
      startDay.date() === 1 &&
      endDay.date() === endDay.daysInMonth() &&
      startDay.month() === endDay.month() &&
      startDay.year() === endDay.year();
    if (isFullCalendarYear) {
      const prev = startDay.subtract(1, "year");
      setSelectedDateRange([
        prev.startOf("year").format("YYYY-MM-DD"),
        prev.endOf("year").format("YYYY-MM-DD"),
      ]);
    } else if (isFullCalendarMonth) {
      const prev = startDay.subtract(1, "month");
      setSelectedDateRange([
        prev.startOf("month").format("YYYY-MM-DD"),
        prev.endOf("month").format("YYYY-MM-DD"),
      ]);
    } else {
      const duration = endDay.diff(startDay, "day") + 1;
      setSelectedDateRange([
        startDay.subtract(duration, "day").format("YYYY-MM-DD"),
        endDay.subtract(duration, "day").format("YYYY-MM-DD"),
      ]);
    }
  };

  const handleNextPeriod = () => {
    const [start, end] = selectedDateRange;
    const startDay = dayjs(start);
    const endDay = dayjs(end);
    const isFullCalendarYear =
      startDay.month() === 0 &&
      startDay.date() === 1 &&
      endDay.month() === 11 &&
      endDay.date() === 31 &&
      startDay.year() === endDay.year();
    const isFullCalendarMonth =
      !isFullCalendarYear &&
      startDay.date() === 1 &&
      endDay.date() === endDay.daysInMonth() &&
      startDay.month() === endDay.month() &&
      startDay.year() === endDay.year();
    if (isFullCalendarYear) {
      const next = startDay.add(1, "year");
      setSelectedDateRange([
        next.startOf("year").format("YYYY-MM-DD"),
        next.endOf("year").format("YYYY-MM-DD"),
      ]);
    } else if (isFullCalendarMonth) {
      const next = startDay.add(1, "month");
      setSelectedDateRange([
        next.startOf("month").format("YYYY-MM-DD"),
        next.endOf("month").format("YYYY-MM-DD"),
      ]);
    } else {
      const duration = endDay.diff(startDay, "day") + 1;
      setSelectedDateRange([
        startDay.add(duration, "day").format("YYYY-MM-DD"),
        endDay.add(duration, "day").format("YYYY-MM-DD"),
      ]);
    }
  };

  const [revenueMode, setRevenueMode] = useState("total");
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedSellerCode, setSelectedSellerCode] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("day");

  const { startDate, endDate } = useMemo(
    () => buildDateRangeParams(selectedDateRange),
    [selectedDateRange]
  );

  const {
    data: filiaisDoCliente,
    isLoading,
    isError,
  } = useHomeFiliais({ enabled: canViewGeral });

  const periodoV2 = useMemo(
    () => periodoSemiaberto(selectedDateRange),
    [selectedDateRange]
  );
  const tipoV2 = tipoDoModo(revenueMode);

  const branchIds = useMemo(
    () => (filiaisDoCliente?.filiais || []).map((item) => item.branch_id),
    [filiaisDoCliente]
  );
  const branchIdsDoGrafico = useMemo(
    () => (selectedBranchId ? [selectedBranchId] : branchIds),
    [selectedBranchId, branchIds]
  );
  const sellersDoGrafico = useMemo(
    () => (selectedSellerCode ? [selectedSellerCode] : []),
    [selectedSellerCode]
  );
  const graficoNoLegado = Boolean(selectedBranchId && selectedSellerCode);

  const {
    data: legacyChartData,
    isLoading: isLegacyChartLoading,
    isError: isLegacyChartError,
  } = useGeneralRevenueGoalChart(
    startDate,
    endDate,
    revenueMode,
    selectedBranchId,
    canViewGeral && graficoNoLegado,
    selectedSellerCode
  );

  const goalsDoGrafico = useHomeGoalsV2({
    branchIds: branchIdsDoGrafico,
    start: periodoV2.start,
    end: periodoV2.end,
    tipo: tipoV2,
    enabled: canViewGeral && !graficoNoLegado,
  });

  const goalsDasFiliais = useHomeGoalsV2({
    branchIds,
    start: periodoV2.start,
    end: periodoV2.end,
    tipo: tipoV2,
    enabled: canViewGeral,
  });

  const faturamentoDasFiliais = useHomeRevenueV2({
    branchIds,
    start: periodoV2.start,
    end: periodoV2.end,
    tipo: tipoV2,
    groupBy: "store",
    enabled: canViewGeral,
  });

  const conversaoDasFiliais = useHomeConversionV2({
    branchIds,
    start: periodoV2.start,
    end: periodoV2.end,
    enabled: canViewGeral,
  });

  const derivadasDasFiliais = useDerivedMetricsV2({
    branchIds,
    start: periodoV2.start,
    end: periodoV2.end,
    tipo: tipoV2,
    enabled: canViewGeral,
  });

  const presencialDoGrafico = useHomeRevenueV2({
    branchIds: branchIdsDoGrafico,
    start: periodoV2.start,
    end: periodoV2.end,
    tipo: "presencial",
    sellers: sellersDoGrafico,
    enabled: canViewGeral,
  });

  const totalDoGrafico = useHomeRevenueV2({
    branchIds: branchIdsDoGrafico,
    start: periodoV2.start,
    end: periodoV2.end,
    tipo: "total",
    sellers: sellersDoGrafico,
    enabled: canViewGeral,
  });

  const goalSeries = useMemo(
    () =>
      graficoNoLegado
        ? serieDeMetaLegado(legacyChartData, dayjs().format("YYYY-MM-DD"))
        : serieDeMetaV2(goalsDoGrafico.data),
    [graficoNoLegado, legacyChartData, goalsDoGrafico.data]
  );

  const isChartLoading = graficoNoLegado
    ? isLegacyChartLoading
    : goalsDoGrafico.isLoading;
  const isChartError = graficoNoLegado
    ? isLegacyChartError
    : goalsDoGrafico.isError;

  const percentPresential = useMemo(
    () => percentualPresencial(presencialDoGrafico.data, totalDoGrafico.data),
    [presencialDoGrafico.data, totalDoGrafico.data]
  );

  const percentualDaMeta = useMemo(
    () => percentualDaMetaPorFilial(goalsDasFiliais.data),
    [goalsDasFiliais.data]
  );

  const tableData = useMemo(() => {
    const faturamento = faturamentoPorFilial(
      faturamentoDasFiliais.data,
      revenueMode
    );
    const conversao = conversaoPorFilial(conversaoDasFiliais.data);
    const derivadas = derivadasPorFilial(derivadasDasFiliais.data);
    return (filiaisDoCliente?.filiais || []).map((item) => ({
      ...item,
      key: item.branch_id,
      faturamento_total: faturamento[item.branch_id]?.faturamento ?? 0,
      ticket_medio: faturamento[item.branch_id]?.ticketMedio ?? 0,
      entradas_clientes: conversao[item.branch_id]?.entradasClientes ?? null,
      entradas_totais: conversao[item.branch_id]?.entradasTotais ?? null,
      entradas_brutas: conversao[item.branch_id]?.entradasBrutas ?? null,
      oportunidades: conversao[item.branch_id]?.oportunidades ?? 0,
      conversao: conversao[item.branch_id]?.conversao ?? null,
      faturamento_por_m2: derivadas[item.branch_id]?.faturamentoPorM2 ?? null,
      permanencia_media: derivadas[item.branch_id]?.atencaoMedia ?? null,
      faturamento_por_tempo_permanencia:
        derivadas[item.branch_id]?.faturamentoPorAtencao ?? null,
    }));
  }, [
    filiaisDoCliente,
    faturamentoDasFiliais.data,
    conversaoDasFiliais.data,
    derivadasDasFiliais.data,
    revenueMode,
  ]);

  const rscoreIds = useMemo(
    () => tableData.map((row) => row.branch_id),
    [tableData]
  );
  const { porFilial: rscoreMap, isLoading: carregandoRscore } = useRScoreV2({
    branchIds: rscoreIds,
    startDate: Array.isArray(selectedDateRange) ? selectedDateRange[0] : null,
    endDate: Array.isArray(selectedDateRange) ? selectedDateRange[1] : null,
  });

  useEffect(() => {
    if (
      selectedBranchId &&
      !tableData.some((item) => item.branch_id === selectedBranchId)
    ) {
      setSelectedBranchId(null);
    }
  }, [selectedBranchId, tableData]);

  useEffect(() => {
    setSelectedSellerCode(null);
  }, [selectedBranchId, startDate, endDate, revenueMode]);

  const { sellers: sellerPerformance, isLoading: isLoadingSellers } =
    useGeneralSellerPerformance(
      selectedBranchId,
      startDate,
      endDate,
      revenueMode,
      canViewGeral
    );

  const derivadasCarregando = derivadasDasFiliais.isLoading;
  const conversaoCarregando = conversaoDasFiliais.isLoading;

  const selectedBranch = useMemo(
    () => tableData.find((item) => item.branch_id === selectedBranchId) || null,
    [selectedBranchId, tableData]
  );

  const columns = [
    {
      title: "Filial",
      dataIndex: "branch_name",
      key: "branch_name",
      width: 240,
      sorter: (a, b) =>
        (a.branch_name || "").localeCompare(b.branch_name || ""),
    },
    {
      title:
        revenueMode === "presencial"
          ? "Faturamento presencial"
          : "Faturamento líquido",
      dataIndex: "faturamento_total",
      key: "faturamento_total",
      width: 190,
      align: "right",
      sorter: (a, b) => a.faturamento_total - b.faturamento_total,
      render: (value, record) => {
        const percent = percentualDaMeta[record.branch_id] ?? null;
        const status = goalAttainmentStatusFromPercent(percent);
        const tooltipTitle =
          percent === null
            ? "Meta não disponível para o período"
            : `${percentFormatter.format(percent)}% da meta atingido`;

        return (
          <Flex align="center" justify="flex-end" gap={8}>
            <span>{currencyFormatter.format(value || 0)}</span>
            <Tooltip title={tooltipTitle}>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  flexShrink: 0,
                  backgroundColor: GOAL_STATUS_COLOR[status],
                }}
              />
            </Tooltip>
          </Flex>
        );
      },
    },
    {
      title: (
        <Tooltip title="Faturamento até ontem ÷ dias corridos × 30 ÷ área.">
          <span>R$/m2/mês</span>
        </Tooltip>
      ),
      dataIndex: "faturamento_por_m2",
      key: "faturamento_por_m2",
      width: 150,
      align: "right",
      sorter: (a, b) =>
        (a.faturamento_por_m2 ?? -1) - (b.faturamento_por_m2 ?? -1),
      render: (value) =>
        celulaDaRota(derivadasCarregando, value, (v) => `R$ ${decimal(v)}`),
    },
    {
      title: "Atenção média",
      dataIndex: "permanencia_media",
      key: "permanencia_media",
      width: 170,
      align: "right",
      sorter: (a, b) =>
        (a.permanencia_media ?? -1) - (b.permanencia_media ?? -1),
      render: (value) =>
        celulaDaRota(derivadasCarregando, value, formatarDuracao),
    },
    {
      title: (
        <Tooltip title="Considera apenas o faturamento presencial dos dias com câmera.">
          <span>Faturamento por Atenção</span>
        </Tooltip>
      ),
      dataIndex: "faturamento_por_tempo_permanencia",
      key: "faturamento_por_tempo_permanencia",
      width: 210,
      align: "right",
      sorter: (a, b) =>
        (a.faturamento_por_tempo_permanencia ?? -1) -
        (b.faturamento_por_tempo_permanencia ?? -1),
      render: (value) =>
        celulaDaRota(derivadasCarregando, value, (v) => `${decimal(v)} R$/min`),
    },
    {
      title: (
        <Tooltip title={HOME_DASHBOARD_METRICS_DESCRIPTION.oportunidades}>
          <span>Oportunidades</span>
        </Tooltip>
      ),
      dataIndex: "oportunidades",
      key: "oportunidades",
      width: 160,
      align: "right",
      sorter: (a, b) => a.oportunidades - b.oportunidades,
      render: (value, record) => {
        if (conversaoCarregando) return <Spin size="small" />;
        const detalhe = detalheDasOportunidades({
          oportunidades: value || 0,
          entradasClientes: record.entradas_clientes,
          entradasTotais: record.entradas_totais,
          entradasBrutas: record.entradas_brutas,
        });
        const numero = numberFormatter.format(value || 0);
        if (!detalhe.length) return numero;
        return (
          <Tooltip
            title={detalhe.map((linha) => (
              <div key={linha}>{linha}</div>
            ))}>
            <span>{numero}</span>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip
          title={HOME_DASHBOARD_METRICS_DESCRIPTION.conversao_oportunidades}>
          <span>Conversão</span>
        </Tooltip>
      ),
      dataIndex: "conversao",
      key: "conversao",
      width: 160,
      align: "right",
      sorter: (a, b) => (a.conversao ?? -1) - (b.conversao ?? -1),
      render: (value) =>
        celulaDaRota(
          conversaoCarregando,
          value,
          (v) =>
            `${Number(v).toLocaleString("pt-BR", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}%`
        ),
    },
    {
      title: "Ticket médio",
      dataIndex: "ticket_medio",
      key: "ticket_medio",
      width: 170,
      align: "right",
      sorter: (a, b) => a.ticket_medio - b.ticket_medio,
      render: (value) => currencyFormatter.format(value || 0),
    },
    {
      title: (
        <Tooltip title="Índice composto de performance (0–100)">
          <span>rSCORE</span>
        </Tooltip>
      ),
      key: "rscore",
      width: 110,
      align: "right",
      sorter: (a, b) => {
        const aScore = rscoreMap[a.branch_id]?.rscore_total ?? -1;
        const bScore = rscoreMap[b.branch_id]?.rscore_total ?? -1;
        return aScore - bScore;
      },
      render: (_, record) => {
        if (carregandoRscore) return <Spin size="small" />;
        const d = rscoreMap[record.branch_id];
        if (!d || d.rscore_total === null)
          return (
            <Text type="secondary" style={{ fontSize: 13 }}>
              --
            </Text>
          );
        const { color } = classeDoRscore(d.classificacao);
        return (
          <Tooltip
            title={
              d.classificacao?.charAt(0).toUpperCase() +
              d.classificacao?.slice(1)
            }>
            <span style={{ color, fontWeight: 600 }}>
              {percentFormatter.format(d.rscore_total)}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const goalProgressMetrics = useMemo(() => {
    const days = goalSeries.days;
    const todayDate = dayjs().format("YYYY-MM-DD");
    if (!days.length) {
      return {
        actualPeriodTotal: 0,
        actualUntilLastSale: 0,
        targetTotalPeriod: 0,
        percentUntilLastSale: null,
        percentPeriodTotal: null,
        lastSaleLabel: null,
        lastSaleIndex: -1,
        periodHasEnded: false,
        faturamentoProjetado: null,
      };
    }

    const lastSaleIndex = goalSeries.ultimaVenda
      ? days.findIndex((day) => day.date === goalSeries.ultimaVenda)
      : -1;
    const targetTotalPeriod = goalSeries.metaPeriodo;
    const actualUntilLastSale = goalSeries.realizadoAteUltimaVenda;
    const percentUntilLastSale = goalSeries.percentAteUltimaVenda;

    return {
      actualPeriodTotal: goalSeries.realizadoPeriodo,
      actualUntilLastSale,
      targetTotalPeriod,
      percentUntilLastSale,
      percentPeriodTotal:
        targetTotalPeriod > 0
          ? (actualUntilLastSale / targetTotalPeriod) * 100
          : null,
      lastSaleLabel: lastSaleIndex >= 0 ? days[lastSaleIndex]?.label : null,
      lastSaleIndex,
      periodHasEnded: days[days.length - 1]?.date < todayDate,
      faturamentoProjetado:
        percentUntilLastSale === null || targetTotalPeriod <= 0
          ? null
          : Math.round(
              ((percentUntilLastSale / 100) * targetTotalPeriod) / 1000
            ) * 1000,
    };
  }, [goalSeries]);

  const goalAttainmentStatus = useMemo(
    () =>
      goalAttainmentStatusFromPercent(goalProgressMetrics.percentUntilLastSale),
    [goalProgressMetrics.percentUntilLastSale]
  );

  const chartDays = goalSeries.days;

  const allDaysCovered =
    chartDays.length > 0 &&
    goalProgressMetrics.lastSaleIndex === chartDays.length - 1;

  const aggregatedChartDays = useMemo(() => {
    if (selectedPeriod === "day" || !chartDays.length) return chartDays;

    const getBucketKey = (dateStr) => {
      if (selectedPeriod === "month") return dateStr.substring(0, 7);
      const d = dayjs(dateStr);
      const dow = d.day();
      return d.subtract(dow === 0 ? 6 : dow - 1, "day").format("YYYY-MM-DD");
    };

    const buckets = {};
    const bucketOrder = [];
    chartDays.forEach((day) => {
      const key = getBucketKey(day.date);
      if (!buckets[key]) {
        buckets[key] = {
          date: day.date,
          label:
            selectedPeriod === "month"
              ? dayjs(day.date).format("MMM/YY")
              : dayjs(day.date).format("DD/MM"),
          actual_revenue: 0,
          target_revenue: 0,
        };
        bucketOrder.push(key);
      }
      buckets[key].actual_revenue += Number(day.actual_revenue || 0);
      buckets[key].target_revenue += Number(day.target_revenue || 0);
    });

    return bucketOrder.map((key) => ({
      ...buckets[key],
      actual_revenue: Math.round(buckets[key].actual_revenue * 100) / 100,
      target_revenue: Math.round(buckets[key].target_revenue * 100) / 100,
    }));
  }, [chartDays, selectedPeriod]);

  const goalMetaLabel =
    selectedPeriod === "week"
      ? "Meta semanal"
      : selectedPeriod === "month"
        ? "Meta mensal"
        : "Meta diária";

  const chartSeries = useMemo(() => {
    const {
      targetTotalPeriod,
      actualUntilLastSale,
      percentUntilLastSale,
      lastSaleIndex: pivotIndex,
      periodHasEnded,
    } = goalProgressMetrics;

    const isBehind =
      selectedPeriod === "day" &&
      pivotIndex >= 0 &&
      percentUntilLastSale !== null &&
      percentUntilLastSale < 100 &&
      !periodHasEnded;

    let revisedData = aggregatedChartDays.map((item) => ({
      x: item.label,
      y: null,
    }));
    if (isBehind) {
      const remainingGap = targetTotalPeriod - actualUntilLastSale;
      const sumRemainingTargets = aggregatedChartDays
        .slice(pivotIndex + 1)
        .reduce((acc, day) => acc + Number(day.target_revenue || 0), 0);
      if (sumRemainingTargets > 0) {
        revisedData = aggregatedChartDays.map((item, i) => {
          if (i <= pivotIndex) return { x: item.label, y: null };
          const t = Number(item.target_revenue || 0);
          return {
            x: item.label,
            y: t > 0 ? (t / sumRemainingTargets) * remainingGap : 0,
          };
        });
      }
    }

    return [
      {
        name:
          revenueMode === "presencial"
            ? "Faturamento presencial"
            : "Faturamento líquido",
        type: "column",
        data: aggregatedChartDays.map((item) => ({
          x: item.label,
          y: item.actual_revenue || 0,
          fillColor: getGoalBarColor(
            Number(item.actual_revenue || 0),
            Number(item.target_revenue || 0)
          ),
        })),
      },
      {
        name: goalMetaLabel,
        type: "line",
        data: aggregatedChartDays.map((item) => ({
          x: item.label,
          y: item.target_revenue || 0,
        })),
      },
      {
        name: "Meta revisada",
        type: "line",
        data: revisedData,
      },
    ];
  }, [
    aggregatedChartDays,
    revenueMode,
    goalProgressMetrics,
    selectedPeriod,
    goalMetaLabel,
  ]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "general-revenue-goal-chart",
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
      },
      colors: ["#7fbf9f", "#64748b", "#9ca3af"],
      stroke: {
        width: [0, 4, 2],
        curve: "smooth",
        dashArray: [0, 0, 6],
      },
      markers: {
        size: [0, 0, 0],
        strokeWidth: 0,
        hover: {
          size: 0,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "52%",
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: "category",
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (value) =>
            Number(value || 0).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
              maximumFractionDigits: 0,
            }),
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value) => currencyFormatter.format(value || 0),
        },
      },
      grid: {
        borderColor: "#e5e7eb",
        strokeDashArray: 4,
      },
    }),
    []
  );

  if (!canViewGeral) {
    return (
      <Result
        status="403"
        title="Acesso restrito"
        subTitle="A página Geral está disponível apenas para Administradores e Gerentes."
      />
    );
  }

  return (
    <Flex
      className="geral-page"
      vertical
      gap={16}
      style={{ minHeight: "100vh" }}>
      <Filters
        page="geral"
        dateRange={true}
        compact={true}
        title={null}
        cardStyle={{ width: "100%" }}
        dateAddon={
          <Flex gap={8}>
            <Button onClick={handlePreviousPeriod}>
              &#8592; Período Anterior
            </Button>
            <Button onClick={handleNextPeriod}>
              Período Posterior &#8594;
            </Button>
          </Flex>
        }
        extraControls={
          <Flex vertical gap={6}>
            <Text strong>Faturamento:</Text>
            <Flex align="center" gap={8}>
              <Text
                type={revenueMode === "presencial" ? undefined : "secondary"}>
                Presencial
              </Text>
              <Switch
                size="small"
                checked={revenueMode === "total"}
                onChange={(checked) =>
                  setRevenueMode(checked ? "total" : "presencial")
                }
              />
              <Text type={revenueMode === "total" ? undefined : "secondary"}>
                Total líquido
              </Text>
            </Flex>
          </Flex>
        }
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        filters={["data"]}
        isMainDataLoading={isLoading}
      />

      <Card className="geral-card">
        <Flex vertical gap={16}>
          <Title level={4} className="geral-title">
            Geral{" "}
            {filiaisDoCliente?.cliente ? `- ${filiaisDoCliente.cliente}` : ""}
          </Title>

          {isLoading || faturamentoDasFiliais.isLoading ? (
            <Spin size="large" style={{ margin: "24px auto" }} />
          ) : isError || faturamentoDasFiliais.isError ? (
            <Alert
              type="error"
              message="Não foi possível carregar o comparativo entre filiais."
              showIcon
            />
          ) : tableData.length === 0 ? (
            <Empty description="Nenhum dado encontrado para o período selecionado." />
          ) : (
            <ConfigProvider
              theme={{
                components: {
                  Table: {
                    headerBg: "#f4f7fb",
                    headerColor: "#1f2a37",
                    borderColor: "#e3e8f0",
                    rowHoverBg: "#f7fbff",
                  },
                },
              }}>
              <Table
                className="geral-table"
                columns={columns}
                dataSource={tableData}
                pagination={false}
                sortDirections={["descend", "ascend"]}
                tableLayout="fixed"
                rowClassName={(record, index) => {
                  const baseClass =
                    index % 2 === 0 ? "geral-row-even" : "geral-row-odd";
                  return record.branch_id === selectedBranchId
                    ? `${baseClass} geral-row-selected`
                    : baseClass;
                }}
                onRow={(record) => ({
                  onClick: () => {
                    setSelectedBranchId((current) =>
                      current === record.branch_id ? null : record.branch_id
                    );
                  },
                  style: { cursor: "pointer" },
                })}
                scroll={{ x: 1510 }}
              />
            </ConfigProvider>
          )}
        </Flex>
      </Card>

      <Card className="geral-card">
        <Flex vertical gap={16}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
            <Flex vertical gap={4}>
              <Title level={5} className="geral-chart-title">
                Meta de faturamento x realizado
              </Title>
              <Text type="secondary">
                {selectedSellerCode
                  ? `Filtrado por ${sellerPerformance?.find((s) => s.erp_code === selectedSellerCode)?.nome || selectedSellerCode}. Clique no vendedor novamente para limpar.`
                  : selectedBranch
                    ? `Filtrado por ${selectedBranch.branch_name}. Clique novamente na linha para limpar.`
                    : "Clique em uma filial na tabela para filtrar este gráfico."}
              </Text>
            </Flex>
            <Flex align="center" gap={12} wrap="wrap">
              <RevenuePeriodSelector
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
              />
              {selectedSellerCode ? (
                <Button onClick={() => setSelectedSellerCode(null)}>
                  Limpar vendedor
                </Button>
              ) : selectedBranchId ? (
                <Button onClick={() => setSelectedBranchId(null)}>
                  Limpar filtro
                </Button>
              ) : null}
            </Flex>
          </Flex>

          {isChartLoading ? (
            <Spin size="large" style={{ margin: "24px auto" }} />
          ) : isChartError ? (
            <Alert
              type="error"
              message="Não foi possível carregar o gráfico de meta de faturamento."
              showIcon
            />
          ) : !chartDays.length ? (
            <Empty description="Nenhum dado encontrado para o período selecionado." />
          ) : (
            <>
              {!goalSeries.hasGoals ? (
                <Alert
                  type="info"
                  showIcon
                  message="Nenhuma meta mensal cadastrada foi encontrada para o período selecionado."
                />
              ) : null}

              <Flex className="geral-chart-kpis" wrap="wrap" gap={12}>
                <div className="geral-chart-kpi">
                  <Text type="secondary">Meta distribuída no período</Text>
                  <Text strong className="geral-chart-kpi-value">
                    {currencyFormatter.format(
                      goalProgressMetrics.targetTotalPeriod
                    )}
                  </Text>
                </div>
                <div className="geral-chart-kpi">
                  <Text type="secondary">
                    {revenueMode === "presencial"
                      ? "Faturamento presencial realizado"
                      : "Faturamento líquido realizado"}
                  </Text>
                  <Text strong className="geral-chart-kpi-value">
                    {currencyFormatter.format(
                      goalProgressMetrics.actualPeriodTotal
                    )}
                  </Text>
                </div>
                {!allDaysCovered && (
                  <div className="geral-chart-kpi">
                    <Text type="secondary">Faturamento projetado</Text>
                    <Text strong className="geral-chart-kpi-value">
                      {goalProgressMetrics.faturamentoProjetado === null
                        ? "--"
                        : currencyFormatter.format(
                            goalProgressMetrics.faturamentoProjetado
                          )}
                    </Text>
                  </div>
                )}
                <div
                  className={`geral-chart-kpi geral-goal-kpi-status geral-goal-kpi-status-${goalAttainmentStatus}`}>
                  <Flex align="center" gap={8}>
                    <Text type="secondary">% meta atingido</Text>
                    {goalAttainmentStatus !== "neutral" ? (
                      <span
                        className={`geral-live-dot geral-live-dot-${goalAttainmentStatus}`}
                      />
                    ) : null}
                  </Flex>
                  <Text strong className="geral-chart-kpi-value">
                    {goalProgressMetrics.percentUntilLastSale === null
                      ? "--"
                      : `${percentFormatter.format(
                          goalProgressMetrics.percentUntilLastSale
                        )}%`}
                  </Text>
                  <Text type="secondary">
                    {goalProgressMetrics.lastSaleLabel
                      ? `Até ${goalProgressMetrics.lastSaleLabel}`
                      : "Sem venda registrada no período"}
                  </Text>
                </div>
                {!allDaysCovered && (
                  <div className="geral-chart-kpi">
                    <Text type="secondary">
                      % meta atingido (período filtrado)
                    </Text>
                    <Text strong className="geral-chart-kpi-value">
                      {goalProgressMetrics.percentPeriodTotal === null
                        ? "--"
                        : `${percentFormatter.format(
                            goalProgressMetrics.percentPeriodTotal
                          )}%`}
                    </Text>
                  </div>
                )}
                <div className="geral-chart-kpi geral-chart-kpi-right">
                  <Text type="secondary">% presencial</Text>
                  <Text strong className="geral-chart-kpi-value">
                    {percentPresential === null
                      ? "--"
                      : `${percentFormatter.format(percentPresential)}%`}
                  </Text>
                </div>
              </Flex>

              <div className="geral-mixed-chart-wrapper">
                <Chart
                  key={`${selectedBranchId}-${selectedSellerCode}-${selectedPeriod}`}
                  options={chartOptions}
                  series={chartSeries}
                  type="line"
                  height={360}
                />
              </div>
            </>
          )}
        </Flex>
      </Card>

      {selectedBranchId && sellerPerformance?.length > 0 && (
        <Card className="geral-card">
          <Flex vertical gap={12}>
            <Title level={5} style={{ margin: 0 }}>
              Performance Vendedores
            </Title>
            {isLoadingSellers ? (
              <Spin size="small" />
            ) : (
              <Table
                size="small"
                dataSource={sellerPerformance.map((s) => ({
                  ...s,
                  key: s.erp_code,
                }))}
                rowClassName={(record) =>
                  record.erp_code === selectedSellerCode
                    ? "geral-seller-row-selected"
                    : ""
                }
                onRow={(record) => ({
                  onClick: () =>
                    setSelectedSellerCode((cur) =>
                      cur === record.erp_code ? null : record.erp_code
                    ),
                  style: { cursor: "pointer" },
                })}
                pagination={false}
                scroll={{ x: 900 }}
                columns={[
                  {
                    title: "Vendedor",
                    dataIndex: "nome",
                    key: "nome",
                    width: 220,
                    fixed: "left",
                  },
                  {
                    title: "Faturamento",
                    dataIndex: "faturamento",
                    key: "faturamento",
                    width: 140,
                    align: "right",
                    render: (v) => currencyFormatter.format(v || 0),
                  },
                  {
                    title: "Fat. Outros",
                    dataIndex: "faturamento_outros",
                    key: "faturamento_outros",
                    width: 130,
                    align: "right",
                    render: (v) => (v > 0 ? currencyFormatter.format(v) : "--"),
                  },
                  {
                    title: "Meta",
                    dataIndex: "meta",
                    key: "meta",
                    width: 140,
                    align: "right",
                    render: (v) => (v > 0 ? currencyFormatter.format(v) : "--"),
                  },
                  {
                    title: "% Atingido",
                    dataIndex: "percent_atingido",
                    key: "percent_atingido",
                    width: 120,
                    align: "right",
                    render: (v) =>
                      v !== null ? `${percentFormatter.format(v)}%` : "--",
                  },
                  {
                    title: "% Atingido (período filtrado)",
                    dataIndex: "percent_atingido_filtrado",
                    key: "percent_atingido_filtrado",
                    width: 200,
                    align: "right",
                    render: (v) =>
                      v !== null ? `${percentFormatter.format(v)}%` : "--",
                  },
                  {
                    title: "Ticket Médio",
                    dataIndex: "ticket_medio",
                    key: "ticket_medio",
                    width: 130,
                    align: "right",
                    render: (v) => currencyFormatter.format(v || 0),
                  },
                  {
                    title: "Preço Médio",
                    dataIndex: "preco_medio",
                    key: "preco_medio",
                    width: 130,
                    align: "right",
                    render: (v) => currencyFormatter.format(v || 0),
                  },
                  {
                    title: "Itens/Atend.",
                    dataIndex: "pecas_por_atendimento",
                    key: "pecas_por_atendimento",
                    width: 110,
                    align: "right",
                    render: (v) =>
                      numberFormatter.format(Number(v || 0).toFixed(1)),
                  },
                ]}
              />
            )}
          </Flex>
        </Card>
      )}

      {selectedBranchId && (
        <Card className="geral-card">
          <RScorePanel
            branchId={selectedBranchId}
            branchName={selectedBranch?.branch_name || ""}
            startDate={
              Array.isArray(selectedDateRange) ? selectedDateRange[0] : null
            }
            endDate={
              Array.isArray(selectedDateRange) ? selectedDateRange[1] : null
            }
          />
        </Card>
      )}
    </Flex>
  );
}
